import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { FormRoot, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { formatIngredientCategoryApiError } from "../api/ingredient-categories-client";
import { useCreateIngredientCategory } from "../api/ingredient-categories-queries";
import {
  IngredientCategoryFormCard,
  type IngredientCategoryFormValues,
} from "../ui/IngredientCategoryFormCard";

const FormSchema = z.object({
  name: z.string().trim().min(1, "ຕ້ອງໃສ່ຊື່ຫມວດ"),
  code: z.string().trim(),
  sortOrder: z.coerce.number().int().min(0),
  description: z.string().trim(),
  isActive: z.boolean(),
});

export function IngredientCategoryCreatePage() {
  const nav = useNavigate({
    from: "/app/inventory/ingredient-categories/create",
  });
  const createCategory = useCreateIngredientCategory();

  const form = RHF.useForm<IngredientCategoryFormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: "",
      code: "",
      sortOrder: 0,
      description: "",
      isActive: true,
    },
  });

  const onSave = form.handleSubmit(async (values) => {
    try {
      await createCategory.mutateAsync({
        name: values.name,
        code: values.code.trim() ? values.code.trim() : null,
        sortOrder: values.sortOrder,
        description: values.description.trim()
          ? values.description.trim()
          : null,
        isActive: values.isActive,
      });
      toast.success("ສ້າງຫມວດສຳເລັດ");
      nav({ to: "/app/inventory/ingredient-categories" });
    } catch (e: unknown) {
      toast.error(formatIngredientCategoryApiError(e));
    }
  });

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຫມວດວັດຖຸດິບ",
              to: "/app/inventory/ingredient-categories",
            },
            { label: "ສ້າງຫມວດ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ສ້າງຫມວດວັດຖຸດິບ</h2>
            <p className="text-muted-foreground">ແຜງຄວບຄຸມ / ຫມວດວັດຖຸດິບ</p>
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/ingredient-categories" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <FormRoot methods={form} onSubmit={onSave} className="space-y-6">
          <IngredientCategoryFormCard form={form} />
        </FormRoot>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t pt-6">
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/inventory/ingredient-categories" })}
          >
            ຍົກເລີກ
          </Button>
          <Button onClick={onSave} isLoading={createCategory.isPending}>
            ສ້າງຫມວດ
          </Button>
        </div>
      </Main>
    </>
  );
}
